import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Updater, useImmer } from "use-immer";

import { IgButton } from "@intelligems/ig-theme/IgButton";
import { IgTooltip } from "@intelligems/ig-theme/IgTooltip";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import {
  CustomEventDto,
  CustomEventFeType,
} from "@intelligems/ig-types/dtos/analytics-events/custom-events";
import { useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import { usePutCustomEvent } from "@src/api/custom-events/put-custom-event";
import {
  createCustomEventPayload,
  getCustomEventsError,
  isSaveButtonDisabled,
} from "@src/features/customEvents/helpers";
import {
  resetNewCustomEvent,
  selectedCurrentCustomEvent,
} from "@src/store/slices/customEvents";
import { closeDrawer } from "@src/store/slices/drawers";
import { setCustomMetrics } from "@src/store/slices/experienceMetrics/experienceMetrics";
import { selectedExperience } from "@src/store/slices/experiences";
import { closeProductPicker } from "@src/store/slices/productPicker";

interface CustomEventDrawerFooterProps {
  subpageSelected: CustomEventFeType | null;
  setSubpageSelected: Updater<CustomEventFeType | null>;
  setIsCreating: Updater<boolean>;
  isCreating: boolean;
  renderLocation: "eventsManager" | "customMetric" | "keyMetric";
  setSelectedGeneric: Updater<string[]>;
  selectedCustomEventIds: string[];
  customEvents: CustomEventDto[];
  setDrawerPage: Updater<string>;
}

export const CustomEventDrawerFooter = ({
  subpageSelected,
  setSubpageSelected,
  setIsCreating,
  isCreating,
  renderLocation,
  setSelectedGeneric,
  selectedCustomEventIds,
  customEvents,
  setDrawerPage,
}: CustomEventDrawerFooterProps) => {
  const dispatch = useDispatch();
  const currentEvent = useSelector(selectedCurrentCustomEvent);
  const experience = useSelector(selectedExperience);
  const theme = useTheme();

  const putCustomEvent = usePutCustomEvent({
    setSubpageSelected,
    setIsCreating,
    setSelectedGeneric,
    renderLocation,
    selectedCustomEventIds,
    dontAddAsSelected: experience ? Boolean(currentEvent?.id) : true,
    setDrawerPage,
  });
  const handleClose = () => {
    dispatch(resetNewCustomEvent());
    setIsCreating(false);
    setSubpageSelected(null);
    dispatch(closeDrawer("customEvents"));
    dispatch(closeProductPicker());
  };
  const handleSave = () => {
    if (subpageSelected) {
      if (currentEvent) {
        putCustomEvent.mutate({
          body: {
            customEvent: createCustomEventPayload(currentEvent),
          },
          pathParams: undefined,
          queryParams: undefined,
        });
      }
    } else {
      dispatch(
        setCustomMetrics({
          selectedCustomEventIds,
          allCustomEvents: customEvents,
        })
      );
      dispatch(closeDrawer("customEvents"));
    }
  };

  const formErrors = getCustomEventsError(currentEvent);

  return (
    <Grid
      container
      direction="column"
      alignItems="flex-end"
      spacing={2}
      sx={{ mt: "auto", py: 5 }}
    >
      {currentEvent?.id && formErrors && (
        <Grid>
          <IgTypography color={theme.palette.text.tonal.error} variant="body2">
            {formErrors}
          </IgTypography>
        </Grid>
      )}
      <Grid>
        <Grid container spacing={2}>
          <Grid>
            <IgButton color="secondary" onClick={handleClose}>
              Cancel
            </IgButton>
          </Grid>
          <Grid>
            <IgTooltip title={formErrors}>
              <IgButton
                variant="contained"
                onClick={handleSave}
                loading={putCustomEvent.isPending}
                disabled={
                  !isCreating &&
                  subpageSelected === null &&
                  selectedCustomEventIds.length === 0
                    ? true
                    : !isCreating &&
                        subpageSelected === null &&
                        selectedCustomEventIds.length > 0
                      ? false
                      : isSaveButtonDisabled(currentEvent)
                }
              >
                Save
              </IgButton>
            </IgTooltip>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
